import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HistoryIcon from '@mui/icons-material/History';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ScienceIcon from '@mui/icons-material/Science';
import GroupsIcon from '@mui/icons-material/Groups';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format as formatJalali } from 'date-fns-jalali';
import { enqueueSnackbar } from 'notistack';
import {
  membership,
  MembershipMember,
  MembershipInvitation,
  MembershipGroup,
  MembershipLimit,
  InvitationStatus,
} from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';
import {
  convertPersianToEnglishNumbers,
  formatPersianNumber,
  phoneNumberValidation,
  toPersianDigits,
} from '../utils/validation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`membership-tabpanel-${index}`}
      aria-labelledby={`membership-tab-${index}`}
      style={{ width: '100%', height: '100%', display: value === index ? 'block' : 'none' }}
    >
      {value === index && (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>{children}</Box>
      )}
    </div>
  );
};

const MEMBERS_COLUMNS = [
  '#',
  'اسم عضو',
  'شماره عضو',
  'ایمیل عضو',
  'نام گروه',
  'تاریخ اختصاص نقش',
  'عملیات',
];
const INVITATIONS_COLUMNS = [
  '#',
  'شماره عضو',
  'ایمیل عضو',
  'نام گروه',
  'تاریخ عضویت',
  'وضعیت عضو',
  'عملیات',
];

const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: 'در انتظار',
  accepted: 'پذیرفته شده',
  revoked: 'لغو شده',
};

const INVITATION_STATUS_COLORS: Record<
  InvitationStatus,
  'warning' | 'success' | 'error'
> = {
  pending: 'warning',
  accepted: 'success',
  revoked: 'error',
};

type InvitationStatusFilter = InvitationStatus | 'all';

const INVITATION_STATUS_FILTERS: {
  value: InvitationStatusFilter;
  label: string;
  selectedColor: string;
  selectedHoverColor: string;
}[] = [
  { value: 'all', label: 'همه', selectedColor: '#009688', selectedHoverColor: '#00796b' },
  { value: 'accepted', label: 'پذیرفته شده', selectedColor: '#2e7d32', selectedHoverColor: '#1b5e20' },
  { value: 'pending', label: 'در انتظار', selectedColor: '#ed6c02', selectedHoverColor: '#e65100' },
  { value: 'revoked', label: 'لغو شده', selectedColor: '#d32f2f', selectedHoverColor: '#b71c1c' },
];

type MemberGroupFilter = 'all' | number;

const MEMBER_GROUP_FILTER_COLORS: Record<number, { selectedColor: string; selectedHoverColor: string }> = {
  1: { selectedColor: '#1565c0', selectedHoverColor: '#0d47a1' },
  2: { selectedColor: '#00695c', selectedHoverColor: '#004d40' },
  3: { selectedColor: '#546e7a', selectedHoverColor: '#37474f' },
  4: { selectedColor: '#78909c', selectedHoverColor: '#607d8b' },
};

const getMemberGroupFilterColors = (group: MembershipGroup) => {
  if (group.is_template && MEMBER_GROUP_FILTER_COLORS[group.id]) {
    return MEMBER_GROUP_FILTER_COLORS[group.id];
  }
  return { selectedColor: '#009688', selectedHoverColor: '#00796b' };
};

const FILTER_AUTO_COLLAPSE_DELAY = 5000;
const FILTER_TRANSITION_MS = 400;

interface FilterBarProps {
  label: string;
  children: React.ReactNode;
  hasActiveFilter?: boolean;
  compact?: boolean;
}

const CollapsibleFilterBar: FC<FilterBarProps> = ({ label, children }) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setCollapsed(true), FILTER_AUTO_COLLAPSE_DELAY);
    return () => window.clearTimeout(timer);
  }, []);

  const handleIconClick = () => {
    if (collapsed) {
      setCollapsed(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: 0.75,
        borderRadius: '24px',
        border: '1px solid rgba(0, 150, 136, 0.35)',
        bgcolor: 'rgba(0, 150, 136, 0.04)',
        flexShrink: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={collapsed ? `نمایش ${label}` : label}
        aria-expanded={!collapsed}
        onClick={handleIconClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          cursor: collapsed ? 'pointer' : 'default',
          color: 'text.secondary',
          width: 40,
          height: 40,
          p: 0,
          borderRadius: '50%',
          flexShrink: 0,
          transition: 'background-color 0.2s ease',
          '&:hover': collapsed ? { bgcolor: 'rgba(0, 0, 0, 0.06)' } : undefined,
        }}
      >
        <FilterListIcon sx={{ fontSize: 24 }} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: collapsed ? '0fr' : '1fr',
          transition: `grid-template-columns ${FILTER_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          minWidth: 0,
        }}
      >
        <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              width: 'max-content',
              maxWidth: { xs: 'calc(100vw - 80px)', sm: 'none' },
              pl: 1,
              opacity: collapsed ? 0 : 1,
              transition: `opacity ${FILTER_TRANSITION_MS}ms ease`,
              pointerEvents: collapsed ? 'none' : 'auto',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', color: 'text.secondary' }}>
              {label}
            </Typography>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MobileFilterModal: FC<FilterBarProps> = ({ label, children, hasActiveFilter, compact }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const useCompact = compact && isXs;

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        aria-label={label}
        sx={{
          border: '1px solid rgba(0, 150, 136, 0.35)',
          bgcolor: hasActiveFilter ? 'rgba(0, 150, 136, 0.12)' : 'rgba(0, 150, 136, 0.04)',
          color: hasActiveFilter ? '#009688' : 'text.secondary',
          width: useCompact ? 40 : 44,
          height: useCompact ? 40 : 44,
          borderRadius: '50%',
          flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(0, 150, 136, 0.08)' },
        }}
      >
        <FilterListIcon sx={{ fontSize: useCompact ? 24 : 26 }} />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2, mx: 1 } }}
      >
        <DialogTitle sx={{ pb: 1.5, fontSize: '1.125rem', fontWeight: 600 }}>{label}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 0.5,
              '& .MuiToggleButtonGroup-root': {
                flexDirection: 'column',
                width: '100%',
                gap: 1.25,
              },
              '& .MuiToggleButtonGroup-grouped': {
                width: '100%',
                justifyContent: 'space-between',
                borderRadius: '24px !important',
              },
              '&& .MuiToggleButton-root': {
                px: 2,
                py: 1.5,
                minHeight: 52,
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                borderRadius: '24px',
                gap: 1,
              },
              '&& .MuiToggleButton-root > span': {
                minWidth: 28,
                height: 28,
                px: 0.75,
                borderRadius: '14px',
                fontSize: '0.8125rem',
                fontWeight: 700,
              },
            }}
          >
            {children}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#009688',
              py: 1.5,
              fontSize: '1rem',
              '&:hover': { bgcolor: '#00796b' },
            }}
          >
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ResponsiveFilterBar: FC<FilterBarProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return <MobileFilterModal {...props} />;
  }

  return <CollapsibleFilterBar {...props} />;
};

const MEMBERSHIP_TABLE_ROW_HEIGHT = 52;

const membershipTableSx = {
  borderCollapse: 'separate',
  borderSpacing: '5px 0',
  '& .MuiTableRow-root': {
    height: MEMBERSHIP_TABLE_ROW_HEIGHT,
  },
  '& .MuiTableCell-root': {
    textAlign: 'center',
    height: MEMBERSHIP_TABLE_ROW_HEIGHT,
    maxHeight: MEMBERSHIP_TABLE_ROW_HEIGHT,
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    py: 0,
    px: 1,
  },
};

const MEMBERS_TABLE_ROW_HEIGHT_XS = 44;

const getMembersTableSx = (isXs: boolean) => ({
  ...membershipTableSx,
  ...(isXs && {
    fontSize: '0.75rem',
  }),
  '& .MuiTableRow-root': {
    height: isXs ? MEMBERS_TABLE_ROW_HEIGHT_XS : MEMBERSHIP_TABLE_ROW_HEIGHT,
  },
  '& .MuiTableCell-root': {
    ...membershipTableSx['& .MuiTableCell-root'],
    ...(isXs && {
      fontSize: '0.75rem',
      lineHeight: 1.35,
    }),
    height: isXs ? MEMBERS_TABLE_ROW_HEIGHT_XS : MEMBERSHIP_TABLE_ROW_HEIGHT,
    maxHeight: isXs ? MEMBERS_TABLE_ROW_HEIGHT_XS : MEMBERSHIP_TABLE_ROW_HEIGHT,
    px: isXs ? 0.5 : 1,
  },
});

const API_PERMISSION_LABELS: Record<string, string> = {
  "history.read" : "مشاهده تاریخچه",
  "conversation.read" : "مشاهده گفتگو دانش",
  "knowledge.read" : "مشاهده دانش",
  "knowledge.write" : "ویرایش دانش",
  "knowledge.query" : "جستجو در دانش",
  "sandbox.chat" : "چت آزمایشی",
  "agent.read" : "مشاهده تنظیمات آیدا",
  "subscription.read" : "مشاهده اشتراک",
  "membership.manage" : "مدیریت عضویت",
  "subscription.manage" : "مدیریت اشتراک",
  "integration.manage" : "مدیریت اطرافیان",
  "settings.manage" : "مدیریت تنظیمات",
  "business.read" : "مشاهده کسب و کار",
  "service.read" : "مشاهده خدمات",
};

const UI_MODULE_LABELS: Record<string, string> = {
  dashboard: 'داشبورد',
  settings: 'تنظیمات آیدا',
  system_prompts: 'وظیفه ها',
  automatic_message: 'کمپین',
  friends: 'اطرافیان', 
  behaivior: 'رفتار',
  channels: 'کانال ها',
  histories: 'تاریخچه گفتگوها',
  profiles: 'پروفایل',
  knowledge: 'صفحه دانش',
  knowledge_list: 'لیست دانش',
  knowledge_conversation: 'گفتگو با دانش',
  knowledge_graph: 'گراف دانش',
  chatbox: 'چت باکس',
  membership: 'عضویت',
  billing: 'پرداخت'
};

interface GroupTierStyle {
  icon: React.ReactNode;
  headerGradient: string;
  headerTextColor: string;
  accentColor: string;
  borderColor: string;
  boxShadow: string;
  chipBg: string;
  chipColor: string;
}

const TEMPLATE_TIER_STYLES: Record<number, GroupTierStyle> = {
  1: {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 28 }} />,
    headerGradient: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #00838f 100%)',
    headerTextColor: '#fff',
    accentColor: '#ffd54f',
    borderColor: 'rgba(255, 213, 79, 0.65)',
    boxShadow: '0 12px 40px rgba(13, 71, 161, 0.35)',
    chipBg: 'rgba(13, 71, 161, 0.1)',
    chipColor: '#0d47a1',
  },
  2: {
    icon: <HistoryIcon sx={{ fontSize: 26 }} />,
    headerGradient: 'linear-gradient(135deg, #004d40 0%, #00695c 50%, #00897b 100%)',
    headerTextColor: '#fff',
    accentColor: '#80cbc4',
    borderColor: 'rgba(0, 150, 136, 0.45)',
    boxShadow: '0 8px 28px rgba(0, 105, 92, 0.28)',
    chipBg: 'rgba(0, 105, 92, 0.1)',
    chipColor: '#00695c',
  },
  3: {
    icon: <MenuBookIcon sx={{ fontSize: 26 }} />,
    headerGradient: 'linear-gradient(135deg, #37474f 0%, #546e7a 60%, #78909c 100%)',
    headerTextColor: '#fff',
    accentColor: '#b0bec5',
    borderColor: 'rgba(84, 110, 122, 0.4)',
    boxShadow: '0 6px 20px rgba(55, 71, 79, 0.22)',
    chipBg: 'rgba(84, 110, 122, 0.12)',
    chipColor: '#37474f',
  },
  4: {
    icon: <ScienceIcon sx={{ fontSize: 24 }} />,
    headerGradient: 'linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)',
    headerTextColor: '#37474f',
    accentColor: '#90a4ae',
    borderColor: 'rgba(176, 190, 197, 0.8)',
    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.08)',
    chipBg: 'rgba(176, 190, 197, 0.25)',
    chipColor: '#455a64',
  },
};

const CUSTOM_GROUP_STYLE: GroupTierStyle = {
  icon: <GroupsIcon sx={{ fontSize: 26 }} />,
  headerGradient: 'linear-gradient(135deg, rgba(0, 150, 136, 0.18) 0%, rgba(0, 150, 136, 0.06) 100%)',
  headerTextColor: '#00695c',
  accentColor: '#009688',
  borderColor: 'rgba(0, 150, 136, 0.35)',
  boxShadow: '0 4px 16px rgba(0, 150, 136, 0.12)',
  chipBg: 'rgba(0, 150, 136, 0.1)',
  chipColor: '#00796b',
};

const getGroupStyle = (group: MembershipGroup): GroupTierStyle => {
  if (group.is_template && TEMPLATE_TIER_STYLES[group.id]) {
    return TEMPLATE_TIER_STYLES[group.id];
  }
  return CUSTOM_GROUP_STYLE;
};

const sortGroups = (groups: MembershipGroup[]) => {
  return [...groups].sort((a, b) => {
    if (a.is_template !== b.is_template) {
      return a.is_template ? -1 : 1;
    }
    if (a.is_template && b.is_template) {
      return a.id - b.id;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

interface GroupCardProps {
  group: MembershipGroup;
  formatDate: (date: string | null) => string;
}

const GroupCard: FC<GroupCardProps> = ({ group, formatDate }) => {
  const style = getGroupStyle(group);
  const isPremiumTemplate = group.is_template && group.id === 1;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '20px',
        border: '2px solid',
        borderColor: style.borderColor,
        boxShadow: style.boxShadow,
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isPremiumTemplate
            ? '0 16px 48px rgba(13, 71, 161, 0.42)'
            : style.boxShadow,
        },
      }}
    >
      <Box
        sx={{
          background: style.headerGradient,
          color: style.headerTextColor,
          px: 2.5,
          py: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isPremiumTemplate ? 'rgba(255, 213, 79, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                color: isPremiumTemplate ? '#ffd54f' : 'inherit',
                flexShrink: 0,
              }}
            >
              {style.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>
                {group.name}
              </Typography>
            </Box>
          </Box>
          {group.is_template && (
            <Tooltip title="گروه پیش‌فرض — قابل ویرایش یا حذف نیست" arrow>
              <Chip
                icon={<LockOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                label="پیش‌فرض"
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: style.headerTextColor,
                  fontWeight: 600,
                  flexShrink: 0,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
          {group.description || '---'}
        </Typography>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <SecurityIcon sx={{ fontSize: 18, color: style.accentColor }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: style.chipColor }}>
              دسترسی‌های سیستمی
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {group.api_permissions.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                دسترسی سیستمی تعریف نشده
              </Typography>
            ) : (
              group.api_permissions.map((permission) => (
                <Chip
                  key={permission}
                  label={API_PERMISSION_LABELS[permission] ?? permission}
                  size="small"
                  sx={{
                    bgcolor: style.chipBg,
                    color: style.chipColor,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              ))
            )}
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <ViewModuleIcon sx={{ fontSize: 18, color: style.accentColor }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: style.chipColor }}>
              بخش‌های پنل
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {group.ui_modules.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                بخشی از پنل در دسترس نیست
              </Typography>
            ) : (
              group.ui_modules.map((module) => (
                <Chip
                  key={module}
                  label={UI_MODULE_LABELS[module] ?? module}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: style.accentColor,
                    color: style.chipColor,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              ))
            )}
          </Box>
        </Box>

        <Divider sx={{ mt: 'auto', display: 'none' }} />

        <Box
          sx={{
            display: 'none',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                تاریخ ایجاد
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                {formatDate(group.created_at)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <UpdateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                آخرین بروزرسانی
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                {formatDate(group.updated_at)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const GroupsTab: FC = () => {
  const { currentLanguage } = useTranslation();
  const [groups, setGroups] = useState<MembershipGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await membership.getGroups();
      setGroups(sortGroups(response.data));
    } catch (error) {
      console.error('Failed to fetch membership groups:', error);
      enqueueSnackbar('خطا در دریافت لیست گروه‌ها', { variant: 'error' });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'HH:mm yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
        }}
      >
        <GroupsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
          هنوز گروهی ایجاد نشده است
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          گروه‌های پیش‌فرض یا سفارشی شما اینجا نمایش داده می‌شوند
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(2, 1fr)',
          xl: 'repeat(2, 1fr)',
        },
        gap: 2.5,
        p: { xs: 1, md: 1.5 },
      }}
    >
      {groups.map((group) => (
        <GroupCard key={group.id} group={group} formatDate={formatDate} />
      ))}
    </Box>
  );
};

const getMemberContact = (member: MembershipMember) => {
  if (member.phone_number?.trim()) return member.phone_number;
  if (member.email?.trim()) return member.email;
  return null;
};

interface MembersTabProps {
  onAddMember: () => void;
}

const MembersTab: FC<MembersTabProps> = ({ onAddMember }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const memberCellSx = isXs ? { fontSize: '0.75rem', lineHeight: 1.35 } : undefined;
  const { currentLanguage } = useTranslation();
  const [members, setMembers] = useState<MembershipMember[]>([]);
  const [groups, setGroups] = useState<MembershipGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingMember, setEditingMember] = useState<MembershipMember | null>(null);
  const [editGroupId, setEditGroupId] = useState<number | ''>('');
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupFilter, setGroupFilter] = useState<MemberGroupFilter>('all');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await membership.getMembers();
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch membership members:', error);
      enqueueSnackbar('خطا در دریافت لیست اعضا', { variant: 'error' });
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const formatCount = (count: number) => {
    return currentLanguage === 'fa' ? formatPersianNumber(count) : String(count);
  };

  const groupFilterOptions = useMemo(() => {
    const options: {
      value: MemberGroupFilter;
      label: string;
      selectedColor: string;
      selectedHoverColor: string;
    }[] = [
      {
        value: 'all',
        label: 'همه',
        selectedColor: '#009688',
        selectedHoverColor: '#00796b',
      },
    ];

    sortGroups(groups).forEach((group) => {
      const colors = getMemberGroupFilterColors(group);
      options.push({
        value: group.id,
        label: group.name,
        ...colors,
      });
    });

    return options;
  }, [groups]);

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = { all: members.length };
    members.forEach((member) => {
      const key = String(member.group_id);
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [members]);

  const filteredMembers = useMemo(() => {
    if (groupFilter === 'all') return members;
    return members.filter((member) => member.group_id === groupFilter);
  }, [members, groupFilter]);

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'HH:mm yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  const formatRowNumber = (index: number) => {
    const value = String(index + 1);
    return currentLanguage === 'fa' ? toPersianDigits(value) : value;
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '---';
    return currentLanguage === 'fa' ? toPersianDigits(phone) : phone;
  };

  const formatContact = (member: MembershipMember) => {
    const contact = getMemberContact(member);
    if (!contact) return '---';
    if (member.phone_number?.trim()) return formatPhone(contact);
    return contact;
  };

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const response = await membership.getGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch membership groups:', error);
      enqueueSnackbar('خطا در دریافت لیست گروه‌ها', { variant: 'error' });
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchGroups();
  }, [fetchMembers, fetchGroups]);

  const handleEditClick = (member: MembershipMember) => {
    setEditingMember(member);
    setEditGroupId(member.group_id);
    if (groups.length === 0) {
      fetchGroups();
    }
  };

  const handleCloseEdit = () => {
    if (saving) return;
    setEditingMember(null);
    setEditGroupId('');
  };

  const handleSaveGroupChange = async () => {
    if (!editingMember || editGroupId === '') return;
    if (editGroupId === editingMember.group_id) {
      enqueueSnackbar('گروه جدید با گروه فعلی یکسان است', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const response = await membership.updateMemberGroup(editingMember.id, {
        group_id: Number(editGroupId),
      });
      const selectedGroup = groups.find((group) => group.id === editGroupId);
      const updatedMember: MembershipMember = response.data ?? {
        ...editingMember,
        group_id: Number(editGroupId),
        group_name: selectedGroup?.name ?? editingMember.group_name,
      };
      setMembers((prev) =>
        prev.map((member) => (member.id === updatedMember.id ? updatedMember : member))
      );
      enqueueSnackbar('گروه عضو با موفقیت تغییر کرد', { variant: 'success' });
      setEditingMember(null);
      setEditGroupId('');
    } catch (error) {
      console.error('Failed to update member group:', error);
      enqueueSnackbar('خطا در تغییر گروه عضو', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      await membership.deleteMember(id);
      enqueueSnackbar('عضو با موفقیت حذف شد', { variant: 'success' });
      setMembers((prev) => prev.filter((member) => member.id !== id));
    } catch (error) {
      console.error('Failed to delete member:', error);
      enqueueSnackbar('خطا در حذف عضو', { variant: 'error' });
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 2, md: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          px: { xs: 0.5, md: 1 },
        }}
      >
        <span>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: isXs ? 22 : 24 }} />}
            onClick={onAddMember}
            sx={{
              bgcolor: '#009688',
              '&:hover': { bgcolor: '#00796b' },
              ...(isXs && {
                width: 'auto !important',
                fontSize: '0.875rem !important',
                py: '8px !important',
                px: '14px !important',
                minHeight: '40px !important',
                '& .MuiButton-startIcon': {
                  mr: '6px !important',
                },
              }),
            }}
          >
            اضافه کردن عضو جدید
          </Button>
        </span>

        <ResponsiveFilterBar label="فیلتر گروه" hasActiveFilter={groupFilter !== 'all'} compact>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={groupFilter}
            onChange={(_event, value: MemberGroupFilter | null) => {
              if (value !== null) setGroupFilter(value);
            }}
            sx={{
              flexWrap: 'wrap',
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '20px !important',
                mx: 0,
              },
            }}
          >
            {groupFilterOptions.map((filter) => (
              <ToggleButton
                key={filter.value}
                value={filter.value}
                sx={{
                  px: { xs: 1.25, sm: 1.75 },
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  fontWeight: 500,
                  borderRadius: '20px',
                  color: 'text.primary',
                  gap: 0.75,
                  '&.Mui-selected': {
                    bgcolor: filter.selectedColor,
                    color: '#fff',
                    '&:hover': { bgcolor: filter.selectedHoverColor },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {filter.label}
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: groupFilter === filter.value ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                    color: groupFilter === filter.value ? '#fff' : 'text.secondary',
                  }}
                >
                  {formatCount(groupCounts[String(filter.value)] ?? 0)}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ResponsiveFilterBar>
      </Box>

      <Paper sx={{ p: 0, width: '100%', overflowX: 'auto' }}>
        <TableContainer>
          <Table size="small" sx={getMembersTableSx(isXs)}>
            <TableHead>
              <TableRow>
                {MEMBERS_COLUMNS.map((column, index) => (
                  <TableCell
                    key={column}
                    sx={{
                      ...memberCellSx,
                      ...(index === 0 && {
                        width: { xs: '36px', sm: '48px' },
                        minWidth: { xs: '36px', sm: '48px' },
                        maxWidth: { xs: '36px', sm: '48px' },
                      }),
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={MEMBERS_COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary">
                      هنوز عضوی ثبت نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={MEMBERS_COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary">
                      عضوی در این گروه یافت نشد.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map((member, index) => (
                  <TableRow key={member.id}>
                    <TableCell
                      sx={{
                        ...memberCellSx,
                        width: { xs: '36px', sm: '48px' },
                        minWidth: { xs: '36px', sm: '48px' },
                        maxWidth: { xs: '36px', sm: '48px' },
                      }}
                    >
                      {formatRowNumber(index)}
                    </TableCell>
                    <TableCell sx={memberCellSx}>{member.full_name || '---'}</TableCell>
                    <TableCell sx={memberCellSx}>{formatPhone(member.phone_number)}</TableCell>
                    <TableCell sx={memberCellSx}>{member.email || '---'}</TableCell>
                    <TableCell sx={memberCellSx}>{member.group_name || '---'}</TableCell>
                    <TableCell sx={memberCellSx}>{formatDate(member.updated_at || member.created_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(member)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(member.id)}
                          disabled={deleting}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>حذف عضو</DialogTitle>
        <DialogContent>
          <Typography>آیا از حذف این عضو اطمینان دارید؟</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>انصراف</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingMember} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>تغییر گروه عضو</DialogTitle>
        <DialogContent>
          {editingMember && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {editingMember.full_name || '---'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatContact(editingMember)}
                </Typography>
              </Box>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    از گروه
                  </Typography>
                  <Chip
                    label={editingMember.group_name || '---'}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: '#fff',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <ArrowBackIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary'}}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    به گروه
                  </Typography>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 180, flex: '1 1 180px' }}
                    disabled={saving || groupsLoading}
                  >
                    <Select
                      value={editGroupId}
                      onChange={(e) => setEditGroupId(e.target.value as number)}
                      displayEmpty
                      sx={{ bgcolor: '#fff' }}
                    >
                      {groups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {groupsLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    در حال بارگذاری گروه‌ها...
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            انصراف
          </Button>
          <Button
            onClick={handleSaveGroupChange}
            variant="contained"
            disabled={
              saving ||
              groupsLoading ||
              editGroupId === '' ||
              editGroupId === editingMember?.group_id
            }
          >
            {saving ? <CircularProgress size={24} /> : 'ذخیره تغییرات'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface InvitationsTabProps {
  openFormOnMount?: boolean;
  onFormMountHandled?: () => void;
}

const InvitationsTab: FC<InvitationsTabProps> = ({ openFormOnMount, onFormMountHandled }) => {
  const { currentLanguage } = useTranslation();
  const [invitations, setInvitations] = useState<MembershipInvitation[]>([]);
  const [groups, setGroups] = useState<MembershipGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [formErrors, setFormErrors] = useState<{
    phone?: string;
    email?: string;
    groupId?: string;
  }>({});
  const [statusFilter, setStatusFilter] = useState<InvitationStatusFilter>('all');
  const [invitationLimit, setInvitationLimit] = useState<MembershipLimit | null>(null);

  const fetchInvitations = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await membership.getInvitations();
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      enqueueSnackbar('خطا در دریافت لیست دعوت‌نامه‌ها', { variant: 'error' });
      setInvitations([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const response = await membership.getGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch membership groups:', error);
      enqueueSnackbar('خطا در دریافت لیست گروه‌ها', { variant: 'error' });
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const fetchLimit = useCallback(async () => {
    try {
      const response = await membership.getLimit();
      setInvitationLimit(response.data);
    } catch (error) {
      console.error('Failed to fetch membership limit:', error);
      enqueueSnackbar('خطا در دریافت سهمیه دعوت‌نامه', { variant: 'error' });
      setInvitationLimit(null);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
    fetchGroups();
    fetchLimit();
  }, [fetchInvitations, fetchGroups, fetchLimit]);

  const canCreateInvitation = (invitationLimit?.remaining ?? 0) > 0;

  const usagePercent = useMemo(() => {
    if (!invitationLimit || invitationLimit.limit <= 0) return 0;
    return Math.min(100, (invitationLimit.used / invitationLimit.limit) * 100);
  }, [invitationLimit]);

  const resetForm = () => {
    setPhone('');
    setEmail('');
    setGroupId('');
    setFormErrors({});
  };

  const handleOpenForm = () => {
    if (!canCreateInvitation) {
      enqueueSnackbar('سهمیه دعوت‌نامه شما تمام شده است', { variant: 'warning' });
      return;
    }
    resetForm();
    setFormOpen(true);
    if (groups.length === 0) {
      fetchGroups();
    }
  };

  const handleCloseForm = () => {
    if (submitting) return;
    setFormOpen(false);
    resetForm();
  };

  useEffect(() => {
    if (!openFormOnMount) return;
    resetForm();
    setFormOpen(true);
    if (groups.length === 0) {
      fetchGroups();
    }
    onFormMountHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFormOnMount]);

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'HH:mm yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  const formatRowNumber = (index: number) => {
    const value = String(index + 1);
    return currentLanguage === 'fa' ? toPersianDigits(value) : value;
  };

  const getGroupName = (id: number) => {
    const group = groups.find((item) => item.id === id);
    return group?.name ?? '---';
  };

  const formatPhone = (phone: string) => {
    return currentLanguage === 'fa' ? toPersianDigits(phone) : phone;
  };

  const getStatusLabel = (status: InvitationStatus) => {
    return INVITATION_STATUS_LABELS[status] ?? status;
  };

  const statusCounts = useMemo(
    () => ({
      all: invitations.length,
      pending: invitations.filter((item) => item.status === 'pending').length,
      accepted: invitations.filter((item) => item.status === 'accepted').length,
      revoked: invitations.filter((item) => item.status === 'revoked').length,
    }),
    [invitations]
  );

  const filteredInvitations = useMemo(() => {
    if (statusFilter === 'all') return invitations;
    return invitations.filter((item) => item.status === statusFilter);
  }, [invitations, statusFilter]);

  const formatCount = (count: number) => {
    return currentLanguage === 'fa' ? formatPersianNumber(count) : String(count);
  };

  const handleDelete = async (invitationId: number) => {
    setDeleting(true);
    try {
      await membership.deleteInvitation(invitationId);
      enqueueSnackbar('دعوت‌نامه با موفقیت حذف شد', { variant: 'success' });
      setInvitations((prev) => prev.filter((invitation) => invitation.id !== invitationId));
      await fetchLimit();
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      enqueueSnackbar('خطا در حذف دعوت‌نامه', { variant: 'error' });
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const validateForm = () => {
    const errors: { phone?: string; email?: string; groupId?: string } = {};
    const trimmedPhone = convertPersianToEnglishNumbers(phone.trim());
    const trimmedEmail = email.trim();

    if (!trimmedPhone) {
      errors.phone = 'شماره تلفن الزامی است';
    }

    if (!trimmedEmail) {
      errors.email = 'ایمیل الزامی است';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = 'ایمیل معتبر وارد کنید';
    }

    if (!groupId) {
      errors.groupId = 'گروه را انتخاب کنید';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitInvitation = async () => {
    if (!canCreateInvitation) {
      enqueueSnackbar('سهمیه دعوت‌نامه شما تمام شده است', { variant: 'warning' });
      return;
    }
    if (!validateForm()) return;

    const phoneResult = phoneNumberValidation(phone);
    const normalizedPhone =
      phoneResult.normalizedPhone ?? convertPersianToEnglishNumbers(phone.trim());

    setSubmitting(true);
    try {
      await membership.createInvitation({
        group_id: Number(groupId),
        phone_number: normalizedPhone,
        email: email.trim(),
      });
      enqueueSnackbar('دعوت‌نامه با موفقیت ارسال شد', { variant: 'success' });
      setFormOpen(false);
      resetForm();
      await Promise.all([fetchInvitations(false), fetchLimit()]);
    } catch (error) {
      console.error('Failed to create invitation:', error);
      enqueueSnackbar('خطا در ارسال دعوت‌نامه', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          mx: { xs: 0.5, md: 1 },
          p: { xs: 1.5, md: 2 },
          borderRadius: '16px',
          border: '1px solid',
          borderColor: canCreateInvitation ? 'rgba(0, 150, 136, 0.35)' : 'rgba(211, 47, 47, 0.35)',
          background: canCreateInvitation
            ? 'linear-gradient(135deg, rgba(0, 150, 136, 0.1) 0%, rgba(0, 150, 136, 0.02) 100%)'
            : 'linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: canCreateInvitation ? 'rgba(0, 150, 136, 0.15)' : 'rgba(211, 47, 47, 0.12)',
                color: canCreateInvitation ? '#00796b' : '#d32f2f',
              }}
            >
              <MailOutlineIcon />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                سهمیه دعوت‌نامه
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {invitationLimit
                  ? `${formatCount(invitationLimit.remaining)} دعوت‌نامه باقی‌مانده`
                  : '---'}
              </Typography>
            </Box>
          </Box>

          {invitationLimit && (
            <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  استفاده‌شده: {formatCount(invitationLimit.used)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  سقف: {formatCount(invitationLimit.limit)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={usagePercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: canCreateInvitation ? '#009688' : '#d32f2f',
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {!canCreateInvitation && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5, fontWeight: 500 }}>
            سهمیه دعوت‌نامه شما تمام شده و امکان ارسال دعوت‌نامه جدید وجود ندارد.
          </Typography>
        )}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          px: { xs: 0.5, md: 1 },
        }}
      >
        <Tooltip
          title={!canCreateInvitation ? 'سهمیه دعوت‌نامه شما تمام شده است' : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenForm}
              disabled={!canCreateInvitation}
              sx={{
                bgcolor: '#009688',
                '&:hover': { bgcolor: '#00796b' },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              دعوت‌نامه جدید
            </Button>
          </span>
        </Tooltip>

        <ResponsiveFilterBar label="فیلتر وضعیت" hasActiveFilter={statusFilter !== 'all'}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={(_event, value: InvitationStatusFilter | null) => {
              if (value) setStatusFilter(value);
            }}
            sx={{
              flexWrap: 'wrap',
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '20px !important',
                mx: 0,
              },
            }}
          >
            {INVITATION_STATUS_FILTERS.map((filter) => (
              <ToggleButton
                key={filter.value}
                value={filter.value}
                sx={{
                  px: { xs: 1.25, sm: 1.75 },
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  fontWeight: 500,
                  borderRadius: '20px',
                  color: 'text.primary',
                  gap: 0.75,
                  '&.Mui-selected': {
                    bgcolor: filter.selectedColor,
                    color: '#fff',
                    '&:hover': { bgcolor: filter.selectedHoverColor },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {filter.label}
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: statusFilter === filter.value ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                    color: statusFilter === filter.value ? '#fff' : 'text.secondary',
                  }}
                >
                  {formatCount(statusCounts[filter.value])}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ResponsiveFilterBar>
      </Box>

      <Paper sx={{ p: 0, width: '100%', overflowX: 'auto' }}>
        <TableContainer>
          <Table size="small" sx={membershipTableSx}>
            <TableHead>
              <TableRow>
                {INVITATIONS_COLUMNS.map((column, index) => (
                  <TableCell
                    key={column}
                    sx={index === 0 ? { width: '48px', minWidth: '48px', maxWidth: '48px' } : undefined}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={INVITATIONS_COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary">
                      هنوز دعوت‌نامه‌ای ارسال نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredInvitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={INVITATIONS_COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary">
                      دعوت‌نامه‌ای با این وضعیت یافت نشد.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvitations.map((invitation, index) => (
                  <TableRow key={invitation.id}>
                    <TableCell sx={{ width: '48px', minWidth: '48px', maxWidth: '48px' }}>
                      {formatRowNumber(index)}
                    </TableCell>
                    <TableCell>{formatPhone(invitation.phone_number)}</TableCell>
                    <TableCell>{invitation.email || '---'}</TableCell>
                    <TableCell>{getGroupName(invitation.group_id)}</TableCell>
                    <TableCell>{formatDate(invitation.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(invitation.status)}
                        color={INVITATION_STATUS_COLORS[invitation.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {invitation.status !== 'revoked' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(invitation.id)}
                          disabled={deleting}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>دعوت‌نامه جدید</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="شماره تلفن عضو جدید"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              fullWidth
              placeholder="09123456789"
              disabled={submitting}
            />
            <TextField
              label="ایمیل عضو جدید"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
              disabled={submitting}
            />
            <FormControl fullWidth error={!!formErrors.groupId} disabled={submitting || groupsLoading}>
              <InputLabel id="invitation-group-label">گروه</InputLabel>
              <Select
                labelId="invitation-group-label"
                label="گروه"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value as number)}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.groupId && <FormHelperText>{formErrors.groupId}</FormHelperText>}
              {groupsLoading && <FormHelperText>در حال بارگذاری گروه‌ها...</FormHelperText>}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={submitting}>
            انصراف
          </Button>
          <Button
            onClick={handleSubmitInvitation}
            variant="contained"
            disabled={submitting || groupsLoading}
            sx={{
              bgcolor: '#009688',
              '&:hover': { bgcolor: '#00796b' },
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'فرستادن دعوت‌نامه'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>حذف دعوت‌نامه</DialogTitle>
        <DialogContent>
          <Typography>آیا از حذف این دعوت‌نامه اطمینان دارید؟</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>انصراف</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Membership: FC = () => {
  const [value, setValue] = useState(0);
  const [pendingInvitationFormOpen, setPendingInvitationFormOpen] = useState(false);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleAddMemberFromMembersTab = useCallback(() => {
    setPendingInvitationFormOpen(true);
    setValue(2);
  }, []);

  const handlePendingInvitationFormHandled = useCallback(() => {
    setPendingInvitationFormOpen(false);
  }, []);

  return (
    <Box sx={{ width: '100%', height: { xs: 'auto', md: '90vh' }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px', px: { xs: 1, md: 0 } }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="membership tabs"
          sx={{
            border: '1.5px solid black',
            borderBottom: 'none',
            borderTopRightRadius: { xs: '10px', sm: '14px', md: '16px' },
            borderTopLeftRadius: { xs: '10px', sm: '14px', md: '16px' },
            '& .MuiTabs-flexContainer': { justifyContent: 'center' },
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              backgroundColor: '#D9D9D980',
              color: '#000',
              fontWeight: 400,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              minWidth: { xs: '60px', sm: '80px', md: '120px' },
              flex: '1 1 0',
              textAlign: 'center',
              fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
              px: { xs: 0.75, sm: 1, md: 2 },
              py: { xs: 0.75, sm: 1, md: 1.25 },
            },
            '& .MuiTab-root:first-of-type': {
              borderTopLeftRadius: { xs: '9px', sm: '13px', md: '15px' },
              borderRight: '1.5px solid black',
            },
            '& .MuiTab-root:nth-of-type(2)': { borderRight: '1.5px solid black' },
            '& .MuiTab-root:last-of-type': {
              borderTopRightRadius: { xs: '9px', sm: '13px', md: '15px' },
            },
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: '#009688',
              color: '#fff',
              fontWeight: 700,
            },
          }}
        >
          <Tab label="اعضا" id="membership-tab-0" aria-controls="membership-tabpanel-0" />
          <Tab label="گروه‌ها" id="membership-tab-1" aria-controls="membership-tabpanel-1" />
          <Tab label="دعوت‌نامه‌ها" id="membership-tab-2" aria-controls="membership-tabpanel-2" />
        </Tabs>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          minHeight: 0,
          border: '1.5px solid black',
          borderRadius: '20px',
          margin: { xs: '0 8px', md: '0 20px' },
        }}
      >
        <TabPanel value={value} index={0}>
          <MembersTab onAddMember={handleAddMemberFromMembersTab} />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <GroupsTab />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <InvitationsTab
            openFormOnMount={pendingInvitationFormOpen}
            onFormMountHandled={handlePendingInvitationFormHandled}
          />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default Membership;
